// ============================================================
// Client/src/components/Dashboard/SLATEChatbot.jsx
// SLATE AI Help Assistant — Role-first, AI-powered, Robot Icon
// ============================================================

import { useState, useEffect, useRef } from 'react';
import { KNOWLEDGE, KEYWORD_MAP } from './ChatbotKnowledge';

const findAnswer = (query) => {
  const q = query.toLowerCase().trim();
  for (const { keys, key } of KEYWORD_MAP) {
    if (keys.some(k => q.includes(k))) return { key, data: KNOWLEDGE[key] };
  }
  return null;
};

const findAIResponse = (query) => {
  const q = query.toLowerCase().trim();
  if (['hi', 'hello', 'hey'].includes(q)) return 'Hello! I am your SLATE Help Assistant. How can I guide you today?';
  if (['who are you', 'what are you', 'bot'].includes(q)) return 'I am an AI-powered assistant designed to help you navigate the SLATE platform.';
  if (['thanks', 'thank you', 'ok', 'okay', 'great'].includes(q)) return 'You\'re very welcome! Let me know if you need anything else.';
  return null;
};

/* ══════════════════════════════════════════════════════════
   QUICK SUGGESTION TOPICS PER ROLE
   ⚠️ VALUES MUST MATCH KEYWORD_MAP KEYS (lowercase)
══════════════════════════════════════════════════════════ */
const SUGGESTIONS = {
  1: [
    { label: 'Getting started',        query: 'getting started' },
    { label: 'Add a school',           query: 'add school' },
    { label: 'Tag lab to school',      query: 'tag lab' },
    { label: 'Create a session',       query: 'create session' },
    { label: 'Add equipment',          query: 'add equipment' },
    { label: 'Equipment allocation',   query: 'allocate equipment' },
    { label: 'HCL Sathee',             query: 'hcl sathee' },
    { label: 'Sathee registration',    query: 'sathee registration' },
    { label: 'My entries & sessions',  query: 'my entries' },
    { label: 'Library',                query: 'library' },
    { label: 'Excel reports',          query: 'excel report' },
    { label: 'PDF reports',            query: 'pdf report' },
    { label: 'Dashboard stats',        query: 'dashboard stat' },
    { label: 'Submit grievance',       query: 'submit grievance' },
    { label: 'Manage grievances',      query: 'manage grievance' },
    { label: 'Add user',               query: 'add user' },
    { label: 'RBAC',                   query: 'rbac' },
    { label: 'System settings',        query: 'system settings' },
    { label: 'God mode',               query: 'god mode' },
  ],
  2: [
    { label: 'Getting started',        query: 'getting started' },
    { label: 'Add a school',           query: 'add school' },
    { label: 'Tag lab to school',      query: 'tag lab' },
    { label: 'Create a session',       query: 'create session' },
    { label: 'Add equipment',          query: 'add equipment' },
    { label: 'Equipment allocation',   query: 'allocate equipment' },
    { label: 'HCL Sathee',             query: 'hcl sathee' },
    { label: 'Sathee registration',    query: 'sathee registration' },
    { label: 'My entries & sessions',  query: 'my entries' },
    { label: 'Library',                query: 'library' },
    { label: 'Excel reports',          query: 'excel report' },
    { label: 'PDF reports',            query: 'pdf report' },
    { label: 'Submit grievance',       query: 'submit grievance' },
    { label: 'My profile',             query: 'my profile' },
  ],
  3: [
    { label: 'Getting started',        query: 'getting started' },
    { label: 'Add a school',           query: 'add school' },
    { label: 'Tag lab to school',      query: 'tag lab' },
    { label: 'Create a session',       query: 'create session' },
    { label: 'Add equipment',          query: 'add equipment' },
    { label: 'Equipment allocation',   query: 'allocate equipment' },
    { label: 'HCL Sathee',             query: 'hcl sathee' },
    { label: 'Sathee registration',    query: 'sathee registration' },
    { label: 'My entries & sessions',  query: 'my entries' },
    { label: 'Library',                query: 'library' },
    { label: 'Excel reports',          query: 'excel report' },
    { label: 'PDF reports',            query: 'pdf report' },
    { label: 'Dashboard stats',        query: 'dashboard stat' },
    { label: 'Submit grievance',       query: 'submit grievance' },
    { label: 'My profile',             query: 'my profile' },
  ],
};

const ROLE_LABELS = { 1: 'Administrator', 2: 'Mentor', 3: 'State Officer' };

/* ══════════════════════════════════════════════════════════
   ROBOT SVG ICON
══════════════════════════════════════════════════════════ */
const RobotIcon = ({ size = 28, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="16" y1="2" x2="16" y2="7" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="16" cy="2" r="1.5" fill={color} />
    <rect x="6" y="7" width="20" height="14" rx="4" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.6" />
    <circle cx="11.5" cy="13" r="2.2" fill={color} />
    <circle cx="20.5" cy="13" r="2.2" fill={color} />
    <circle cx="12.2" cy="12.3" r="0.7" fill="rgba(30,58,138,0.7)" />
    <circle cx="21.2" cy="12.3" r="0.7" fill="rgba(30,58,138,0.7)" />
    <path d="M11 17.5 Q16 20.5 21 17.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" />
    <rect x="3" y="10" width="3" height="5" rx="1.5" fill={color} fillOpacity="0.7" />
    <rect x="26" y="10" width="3" height="5" rx="1.5" fill={color} fillOpacity="0.7" />
    <rect x="11" y="21" width="10" height="5" rx="2" fill={color} fillOpacity="0.25" stroke={color} strokeWidth="1.2" />
    <line x1="16" y1="21" x2="16" y2="26" stroke={color} strokeWidth="1.2" />
  </svg>
);

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
const SLATEChatbot = () => {
  const [open,      setOpen]      = useState(false);
  const [messages,  setMessages]  = useState([]);
  const [input,     setInput]     = useState('');
  const [roleId,    setRoleId]    = useState(null);
  const [initiated, setInitiated] = useState(false);
  const [btnHover,  setBtnHover]  = useState(false); // ← FIX 1: hover state for tooltip
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);
  const msgLenRef = useRef(0);

  useEffect(() => {
    if (messages.length !== msgLenRef.current) {
      msgLenRef.current = messages.length;
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (open && roleId) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open, roleId]);

  useEffect(() => {
    if (open && !initiated) {
      setInitiated(true);
      setMessages([{ type: 'role-select', time: new Date() }]);
    }
  }, [open, initiated]);

  const selectRole = (id) => {
    setRoleId(id);
    setMessages(prev => [...prev,
      { type: 'user', content: ROLE_LABELS[id], time: new Date() },
      {
        type: 'assistant',
        content: `Got it! You are logged in as **${ROLE_LABELS[id]}**.\n\nI will guide you based on your role and access level. Select a topic below or type your question.\n\nFor example:\n- "How to create a session"\n- "How to download PDF reports"\n- "How to submit a grievance"`,
        time: new Date(),
        showSuggestions: true,
        roleId: id,
      },
    ]);
  };

  /* ── send message ───────────────────────────────────── */
  const sendMessage = (text) => {
    const query = (text || input).trim();
    if (!query) return;
    setInput('');

    const aiReply = findAIResponse(query);
    if (aiReply) {
      setMessages(prev => [...prev,
        { type: 'user', content: query, time: new Date() },
        { type: 'assistant', content: aiReply, time: new Date() },
      ]);
      return;
    }

    const found = findAnswer(query);
    let botMsg;

    if (found) {
      const { data } = found;
      const hasAccess = !data.roles || data.roles.includes(roleId);
      if (!hasAccess) {
        botMsg = {
          type: 'assistant',
          content: `🔒 **Access Restricted**\n\nThe feature "${data.title}" is not available for your role (${ROLE_LABELS[roleId]}).\n\nPlease contact your Administrator if you need access to this feature.`,
          time: new Date(),
        };
      } else {
        botMsg = { type: 'guide', data, time: new Date() };
      }
    } else {
      botMsg = {
        type: 'assistant',
        content: `I couldn't find a guide for that.\n\nHere are things I can help with:\n\n- Add School / Tag Lab / Add Equipment\n- Equipment Allocation / Session Setup\n- Sathee Registration / My Entries\n- Library (Upload / Search / Add URL)\n- Excel Reports / PDF Reports\n- Submit Grievance / Manage Grievances\n- God Mode (Admin only)\n- Getting Started\n\nTry typing one of the above!`,
        time: new Date(),
      };
    }

    setMessages(prev => [...prev,
      { type: 'user', content: query, time: new Date() },
      botMsg,
    ]);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearChat = () => {
    setMessages([]);
    setRoleId(null);
    setInitiated(false);
    msgLenRef.current = 0;
  };

  const fmtTime = d => d?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  /* ── sub-components ─────────────────────────────────── */
  const Avatar = () => (
    <div style={{
      width: 26, height: 26, borderRadius: 7, flexShrink: 0, marginTop: 2,
      background: 'linear-gradient(135deg,#1e3a8a,#2563eb)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <RobotIcon size={17} color="#fff" />
    </div>
  );

  const Bubble = ({ children }) => (
    <div style={{
      background: '#fff', color: '#1e293b',
      borderRadius: '12px 12px 12px 3px',
      padding: '11px 13px', lineHeight: 1.65,
      boxShadow: '0 1px 6px rgba(0,0,0,.08)',
      border: '1px solid #e2e8f0',
    }}>{children}</div>
  );

  const MsgTime = ({ children }) => (
    <p style={{ margin: '3px 0 0', fontSize: 9.5, color: '#94a3b8' }}>{children}</p>
  );

  const renderText = (content) =>
    content.split('\n').map((line, li) => {
      const html = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      if (line.startsWith('- ')) return (
        <div key={li} style={{ display: 'flex', gap: 7, marginBottom: 3 }}>
          <span style={{ color: '#2563eb', fontWeight: 700, flexShrink: 0 }}>—</span>
          <span style={{ fontSize: 12.5, color: '#1e293b', lineHeight: 1.65 }}
            dangerouslySetInnerHTML={{ __html: html.slice(2) }} />
        </div>
      );
      if (line.trim() === '') return <div key={li} style={{ height: 4 }} />;
      return (
        <div key={li} style={{ fontSize: 12.5, color: '#1e293b', lineHeight: 1.65 }}
          dangerouslySetInnerHTML={{ __html: html }} />
      );
    });

  /* ── render each message ────────────────────────────── */
  const renderMsg = (msg, i) => {

    if (msg.type === 'role-select') return (
      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start' }}>
        <Avatar />
        <div style={{ flex: 1 }}>
          <Bubble>
            <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#1e293b', lineHeight: 1.6 }}>
              Welcome to <strong>SLATE AI Help Assistant</strong> 👋<br />
              Please select your role so I can give you accurate guidance:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {[
                { id: 1, label: 'Administrator', desc: 'Full access — all features, God Mode, 26 reports', icon: 'bx-shield' },
                { id: 2, label: 'Mentor',        desc: 'Lab-level access — your assigned lab, 11 reports', icon: 'bx-user' },
                { id: 3, label: 'State Officer', desc: 'State-level access — your state only, 22 reports', icon: 'bx-map' },
              ].map(r => (
                <button key={r.id} onClick={() => selectRole(r.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 11,
                  background: '#f8fafc', border: '1.5px solid #e2e8f0',
                  borderRadius: 10, padding: '9px 13px',
                  cursor: 'pointer', transition: 'all .18s', textAlign: 'left',
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#1e3a8a';
                    e.currentTarget.style.borderColor = '#1e3a8a';
                    e.currentTarget.querySelector('.rl').style.color = '#fff';
                    e.currentTarget.querySelector('.rd').style.color = 'rgba(255,255,255,.75)';
                    e.currentTarget.querySelector('.ri').style.color = '#fff';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.querySelector('.rl').style.color = '#1e293b';
                    e.currentTarget.querySelector('.rd').style.color = '#64748b';
                    e.currentTarget.querySelector('.ri').style.color = '#1e3a8a';
                  }}>
                  <i className={`bx ${r.icon} ri`} style={{ fontSize: 18, color: '#1e3a8a', flexShrink: 0 }} />
                  <div>
                    <p className="rl" style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: '#1e293b' }}>{r.label}</p>
                    <p className="rd" style={{ margin: 0, fontSize: 11, color: '#64748b' }}>{r.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </Bubble>
          <MsgTime>{fmtTime(msg.time)}</MsgTime>
        </div>
      </div>
    );

    if (msg.type === 'user') return (
      <div key={i} style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <div>
          <div style={{
            background: '#1e3a8a', color: '#fff',
            borderRadius: '12px 12px 3px 12px',
            padding: '9px 13px', fontSize: 12.5, lineHeight: 1.6,
            boxShadow: '0 2px 8px rgba(30,58,138,.2)', maxWidth: 280,
          }}>{msg.content}</div>
          <p style={{ margin: '3px 0 0', fontSize: 9.5, color: '#94a3b8', textAlign: 'right' }}>{fmtTime(msg.time)}</p>
        </div>
      </div>
    );

    if (msg.type === 'assistant') return (
      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start' }}>
        <Avatar />
        <div style={{ maxWidth: '88%' }}>
          <Bubble>
            {renderText(msg.content)}
            {msg.showSuggestions && msg.roleId && (
              <div style={{ marginTop: 12 }}>
                <p style={{ margin: '0 0 7px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .7 }}>
                  Quick Help Topics
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {/* ← FIX 2: use .query for sendMessage, .label for display */}
                  {(SUGGESTIONS[msg.roleId] || []).map((s, si) => (
                    <button key={si} onClick={() => sendMessage(s.query)} style={{
                      background: '#eff6ff', border: '1px solid #bfdbfe',
                      borderRadius: 6, padding: '4px 10px',
                      fontSize: 11, color: '#1e3a8a', cursor: 'pointer',
                      fontWeight: 500, transition: 'all .15s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#1e3a8a'; e.currentTarget.style.color = '#fff'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#1e3a8a'; }}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Bubble>
          <MsgTime>{fmtTime(msg.time)}</MsgTime>
        </div>
      </div>
    );

    if (msg.type === 'guide') {
      const { data } = msg;
      return (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start' }}>
          <Avatar />
          <div style={{ flex: 1 }}>
            <div style={{
              background: '#fff', borderRadius: '12px 12px 12px 3px',
              boxShadow: '0 2px 10px rgba(0,0,0,.08)',
              border: '1px solid #e2e8f0', overflow: 'hidden',
            }}>
              <div style={{ background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', padding: '10px 14px' }}>
                <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: '#fff', letterSpacing: .2 }}>
                  📖 {data.title}
                </p>
              </div>
              <div style={{ padding: '12px 14px' }}>
                <p style={{ margin: '0 0 9px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: .7 }}>
                  Step-by-step Guide
                </p>
                {(() => {
                  let stepNum = 0;
                  return data.steps.map((step, si) => {
                    if (step === '') return <div key={si} style={{ height: 6 }} />;
                    if (
                      step.startsWith('📂') ||
                      step.startsWith('  •') ||
                      step.startsWith('  🟣') ||
                      step.startsWith('  🔵') ||
                      step.startsWith('  🟦') ||
                      step.startsWith('  🟠') ||
                      step.startsWith('  🟢')
                    ) {
                      return (
                        <div key={si} style={{
                          fontSize: step.startsWith('  ') ? 11.5 : 12,
                          color: step.startsWith('  ') ? '#475569' : '#1e3a8a',
                          fontWeight: step.startsWith('  ') ? 400 : 700,
                          marginBottom: 4,
                          paddingLeft: step.startsWith('  ') ? 14 : 0,
                          lineHeight: 1.6,
                        }}>{step}</div>
                      );
                    }
                    if (
                      step.startsWith('— ') ||
                      step.startsWith('⚙️') || step.startsWith('👁️') ||
                      step.startsWith('👤') || step.startsWith('🔒') ||
                      step.startsWith('🧪') || step.startsWith('📄') ||
                      step.startsWith('🗂️') || step.startsWith('🏷️') ||
                      step.startsWith('🏫') || step.startsWith('📢')
                    ) {
                      return (
                        <div key={si} style={{
                          fontSize: 12, color: '#1e40af', fontWeight: 600,
                          marginBottom: 5, lineHeight: 1.6,
                        }}>{step}</div>
                      );
                    }
                    stepNum++;
                    return (
                      <div key={si} style={{ display: 'flex', gap: 10, marginBottom: 7, alignItems: 'flex-start' }}>
                        <div style={{
                          width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                          background: '#eff6ff', border: '1.5px solid #bfdbfe',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 700, color: '#1e3a8a',
                        }}>{stepNum}</div>
                        <p style={{ margin: 0, fontSize: 12, color: '#334155', lineHeight: 1.6 }}>{step}</p>
                      </div>
                    );
                  });
                })()}
                {data.note && (
                  <div style={{
                    marginTop: 10, padding: '8px 11px',
                    background: '#f0f9ff', borderRadius: 8,
                    borderLeft: '3px solid #2563eb',
                  }}>
                    <p style={{ margin: 0, fontSize: 11, color: '#1e40af', lineHeight: 1.5 }}>
                      <strong>💡 Note:</strong> {data.note}
                    </p>
                  </div>
                )}
                <p style={{ margin: '12px 0 0', fontSize: 11, color: '#94a3b8' }}>
                  Need help with something else? Type your question below.
                </p>
              </div>
            </div>
            <MsgTime>{fmtTime(msg.time)}</MsgTime>
          </div>
        </div>
      );
    }

    return null;
  };

  /* ══════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════ */
  return (
    <>
      {/* ── TOOLTIP — only on hover (FIX 1) ── */}
      {!open && btnHover && (
        <div style={{
          position: 'fixed', bottom: 96, right: 28, zIndex: 9000,
          background: '#1e3a8a', color: '#fff',
          fontSize: 10.5, fontWeight: 700, letterSpacing: .4,
          padding: '5px 11px', borderRadius: 20,
          boxShadow: '0 2px 10px rgba(30,58,138,.4)',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          animation: 'slateUp .2s ease',
        }}>
          SLATE AI Help Assistant ✨
          <div style={{
            position: 'absolute', bottom: -5, right: 20,
            width: 10, height: 6,
            background: '#1e3a8a',
            clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
          }} />
        </div>
      )}

      {/* ── FLOATING BUTTON ── */}
      <div
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setBtnHover(true)}
        onMouseLeave={() => setBtnHover(false)}
        title="SLATE AI Help Assistant"
        style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 9000,
          width: 58, height: 58, borderRadius: 16,
          background: open ? '#1e293b' : 'linear-gradient(135deg,#1e3a8a,#2563eb)',
          boxShadow: open ? '0 4px 20px rgba(0,0,0,.3)' : '0 6px 28px rgba(37,99,235,.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'all .25s',
          border: '2px solid rgba(255,255,255,.18)',
        }}
      >
        {open
          ? <i className="bx bx-x" style={{ fontSize: 26, color: '#fff' }} />
          : <RobotIcon size={30} color="#fff" />
        }
        {!open && (
          <div style={{
            position: 'absolute', top: -6, right: -6,
            background: 'linear-gradient(135deg,#f59e0b,#ef4444)',
            color: '#fff', fontSize: 8.5, fontWeight: 800,
            padding: '2px 5px', borderRadius: 8,
            border: '1.5px solid #fff', letterSpacing: .5,
            boxShadow: '0 2px 6px rgba(0,0,0,.2)',
          }}>AI</div>
        )}
        {!open && (
          <div style={{
            position: 'absolute', bottom: 6, right: 6,
            width: 8, height: 8, borderRadius: '50%',
            background: '#22c55e', border: '2px solid #fff',
          }} />
        )}
      </div>

      {/* ── CHAT PANEL ── */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 98, right: 28, zIndex: 9001,
          width: Math.min(440, window.innerWidth - 40),
          height: Math.min(640, window.innerHeight - 120),
          background: '#fff', borderRadius: 16,
          boxShadow: '0 24px 64px rgba(0,0,0,.16), 0 0 0 1px rgba(0,0,0,.06)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          animation: 'slateUp .2s ease',
        }}>

          {/* HEADER */}
          <div style={{
            background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)',
            padding: '13px 15px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: 'rgba(255,255,255,.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid rgba(255,255,255,.2)',
              }}>
                <RobotIcon size={24} color="#fff" />
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: .2 }}>
                    SLATE AI Help Assistant
                  </p>
                  <span style={{
                    background: 'linear-gradient(135deg,#f59e0b,#ef4444)',
                    color: '#fff', fontSize: 8, fontWeight: 800,
                    padding: '1px 5px', borderRadius: 5, letterSpacing: .5,
                  }}>AI</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80' }} />
                  <p style={{ margin: 0, fontSize: 10.5, color: 'rgba(255,255,255,.75)' }}>
                    {roleId ? `${ROLE_LABELS[roleId]} · Platform Guide` : 'Select your role to begin'}
                  </p>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={clearChat} title="Reset Chat" style={{
                background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)',
                borderRadius: 7, width: 30, height: 30, color: '#fff',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.22)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,.1)'}>
                <i className="bx bx-rotate-left" style={{ fontSize: 14 }} />
              </button>
              <button onClick={() => setOpen(false)} title="Close" style={{
                background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)',
                borderRadius: 7, width: 30, height: 30, color: '#fff',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,.5)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,.1)'}>
                <i className="bx bx-x" style={{ fontSize: 16 }} />
              </button>
            </div>
          </div>

          {/* MESSAGES */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '14px 12px',
            background: '#f8fafc', display: 'flex', flexDirection: 'column',
          }}>
            {messages.map((msg, i) => renderMsg(msg, i))}
            <div ref={bottomRef} />
          </div>

          {/* INPUT BAR */}
          <div style={{
            padding: '10px 12px 12px',
            borderTop: '1px solid #e2e8f0',
            background: '#fff', flexShrink: 0,
          }}>
            <div style={{
              display: 'flex', gap: 8, alignItems: 'center',
              background: roleId ? '#f8fafc' : '#f1f5f9',
              border: '1.5px solid #e2e8f0',
              borderRadius: 10, padding: '7px 10px',
              opacity: roleId ? 1 : 0.65,
            }}>
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={roleId ? 'Type your question here…' : 'Please select your role above first…'}
                disabled={!roleId}
                style={{
                  flex: 1, border: 'none', background: 'transparent',
                  outline: 'none', fontSize: 12.5, color: '#1e293b',
                  fontFamily: 'inherit', cursor: roleId ? 'text' : 'not-allowed',
                }}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || !roleId}
                style={{
                  width: 32, height: 32, borderRadius: 8, border: 'none',
                  background: input.trim() && roleId ? '#1e3a8a' : '#e2e8f0',
                  color: input.trim() && roleId ? '#fff' : '#94a3b8',
                  cursor: input.trim() && roleId ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all .2s', flexShrink: 0,
                }}>
                <i className="bx bx-send" style={{ fontSize: 14 }} />
              </button>
            </div>
            <p style={{
              margin: '6px 0 0', fontSize: 9.5, color: '#94a3b8',
              textAlign: 'center', letterSpacing: .3,
            }}>
              ✨ Powered by AI · SLATE Platform Guide
            </p>
          </div>

        </div>
      )}

      <style>{`
        @keyframes slateUp {
          from { opacity: 0; transform: translateY(14px) scale(.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};

export default SLATEChatbot;
